import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'
import { useState } from 'react'

import { IconButton } from '@/components/ui/IconButton'
import { Toggle } from '@/components/ui/Toggle'
import { reorder, setFormatDisabled, videoFormatLabel } from '@/lib/videoFormats'

import styles from './PriorityFormatList.module.scss'

interface Props {
  factory: string
  /** Ordem atual, do mais para o menos prioritário. Slugs de ``video_type``. */
  value: ReadonlyArray<string>
  onChange: (next: string[]) => void
  /** Slugs desligados: nem a geração nem o upload os usam. */
  disabled: ReadonlyArray<string>
  onDisabledChange: (next: string[]) => void
}

const LAST_ACTIVE_HINT = 'Pelo menos um formato fica ativo. Para parar o canal, use o "Ativo" do topo.'

/** Lista de formatos que o operador reordena arrastando e liga ou desliga.
 *
 * Arrastar é o gesto natural aqui, mas sozinho ele exclui quem navega por
 * teclado — daí os botões de subir/descer ao lado, que também são mais
 * precisos com dois ou três itens. Os dois caminhos chamam o mesmo `reorder`.
 *
 * Desligar não move o item na hora, para a linha não fugir de debaixo do
 * cursor: o modal manda os desligados para o fim ao salvar. Até lá, a posição
 * mostrada já é a que vale, contando só os ligados.
 */
export function PriorityFormatList({ factory, value, onChange, disabled, onDisabledChange }: Props) {
  const [dragging, setDragging] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)

  if (value.length === 0) {
    return (
      <span className={styles.empty}>
        A factory <code>{factory}</code> tem um formato só — não há o que ordenar.
      </span>
    )
  }

  const drop = (to: number) => {
    if (dragging !== null) onChange(reorder(value, dragging, to))
    setDragging(null)
    setOver(null)
  }

  const activeCount = value.filter((slug) => !disabled.includes(slug)).length
  let position = 0

  return (
    <ol className={styles.list} aria-label="Ordem de prioridade dos formatos">
      {value.map((slug, i) => {
        const label = videoFormatLabel(factory, slug)
        const active = !disabled.includes(slug)
        if (active) position += 1
        const lastActive = active && activeCount === 1
        return (
          <li
            key={slug}
            className={[
              styles.item,
              active ? '' : styles.off,
              dragging === i ? styles.dragging : '',
              over === i && dragging !== i ? styles.over : '',
            ]
              .filter(Boolean)
              .join(' ')}
            draggable
            onDragStart={() => {
              setDragging(i)
            }}
            onDragEnd={() => {
              setDragging(null)
              setOver(null)
            }}
            onDragOver={(e) => {
              // Sem isto o browser recusa o drop e o cursor mostra "proibido".
              e.preventDefault()
              setOver(i)
            }}
            onDrop={(e) => {
              e.preventDefault()
              drop(i)
            }}
          >
            <GripVertical className={styles.grip} aria-hidden size={14} />
            <span className={styles.position}>{active ? position : '–'}</span>
            <span className={styles.name}>
              {label}
              {active ? null : <span className={styles.offTag}>desativado</span>}
            </span>
            <code className={styles.slug}>{slug}</code>
            <span className={styles.actions}>
              <IconButton
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Subir ${label}`}
                disabled={i === 0}
                onClick={() => {
                  onChange(reorder(value, i, i - 1))
                }}
              >
                <ArrowUp size={14} />
              </IconButton>
              <IconButton
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Descer ${label}`}
                disabled={i === value.length - 1}
                onClick={() => {
                  onChange(reorder(value, i, i + 1))
                }}
              >
                <ArrowDown size={14} />
              </IconButton>
            </span>
            {/* O title fica no wrapper: botão desabilitado não recebe hover. */}
            <span className={styles.toggle} title={lastActive ? LAST_ACTIVE_HINT : undefined}>
              <Toggle
                aria-label={`${label} ativo`}
                checked={active}
                disabled={lastActive}
                onCheckedChange={(on) => {
                  onDisabledChange(setFormatDisabled(disabled, slug, !on))
                }}
              />
            </span>
          </li>
        )
      })}
    </ol>
  )
}
